import React from "react";
import find from "lodash/find";
import isEqual from "lodash/isEqual";
import { ActionCreator } from "easy-peasy";
import { useUnmount, useUpdateEffect } from "react-use";
import { useLocation, useSearchParams } from "react-router-dom";
import { useComponentWillMount } from "app/hooks/useCompWillMount";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { TRANSACTION_TYPES } from "app/pages/explorer/components/transaction-type/data";
import {
  defaultAppliedFilters,
  AppliedFiltersStateModel,
} from "app/state/api/action-reducers/explorer/appliedFilters";

const apiFilterFields = {
  startDate: "startDate",
  endDate: "endDate",
  country: "country",
  publisher: "publisher",
  sector: "sector",
  activityStatus: "activityStatus",
  transactionType: "transactionType",
  amountMin: "amountMin",
  amountMax: "amountMax",
  sdg: "sdg",
  search: "search",
  hasSdg: "hasSdg",
  activityScope: "activityScope",
  documentLinkCategory: "documentLinkCategory",
  hierarchy: "hierarchy",
  humanitarianScopeVocabulary: "humanitarianScopeVocabulary",
  humanitarianScopeType: "humanitarianScopeType",
  iatiVersion: "iatiVersion",
  otherIdentifierType: "otherIdentifierType",
  tag: "tag",
  defaultAidType: "defaultAidType",
  defaultAidTypeVocabulary: "defaultAidTypeVocabulary",
  currency: "currency",
  defaultFinanceType: "defaultFinanceType",
  defaultFlowType: "defaultFlowType",
  defaultLanguage: "defaultLanguage",
  defaultTiedStatus: "defaultTiedStatus",
  transactionReceiverOrganisation: "transactionReceiverOrganisation",
  reportingOrganisationType: "reportingOrganisationType",
  collaborationType: "collaborationType",
  secondaryReporter: "secondaryReporter",
  sectorVocabulary: "sectorVocabulary",
  policyMarker: "policyMarker",
  transactionFlowType: "transactionFlowType",
  transactionTiedStatus: "transactionTiedStatus",
  transactionHumanitarian: "transactionHumanitarian",
  transactionValueCurrency: "transactionValueCurrency",
};

// Pure function to get normalized filters from URL params
export function getNormalizedFiltersFromUrlParams(
  urlParams: URLSearchParams,
  selectedFilters: AppliedFiltersStateModel
): AppliedFiltersStateModel {
  const updatedSelectedFilters = { ...selectedFilters };
  const startDate = urlParams.get(apiFilterFields.startDate);
  const endDate = urlParams.get(apiFilterFields.endDate);
  const countries = urlParams.get(apiFilterFields.country);
  const sectors = urlParams.get(apiFilterFields.sector);
  const publishers = urlParams.get(apiFilterFields.publisher);
  const activityStatus = urlParams.get(apiFilterFields.activityStatus);
  const amountMin = urlParams.get(apiFilterFields.amountMin);
  const amountMax = urlParams.get(apiFilterFields.amountMax);
  const transactionType = urlParams.get(apiFilterFields.transactionType);
  const search = urlParams.get(apiFilterFields.search);
  const hasSdg = urlParams.get(apiFilterFields.hasSdg);
  const sdgs = urlParams.get(apiFilterFields.sdg);
  const activityScope = urlParams.get(apiFilterFields.activityScope);
  const documentLinkCategory = urlParams.get(
    apiFilterFields.documentLinkCategory
  );
  const hierarchy = urlParams.get(apiFilterFields.hierarchy);
  const humanitarianScopeVocabulary = urlParams.get(
    apiFilterFields.humanitarianScopeVocabulary
  );
  const humanitarianScopeType = urlParams.get(
    apiFilterFields.humanitarianScopeType
  );
  const iatiVersion = urlParams.get(apiFilterFields.iatiVersion);
  const otherIdentifierType = urlParams.get(
    apiFilterFields.otherIdentifierType
  );
  const tag = urlParams.get(apiFilterFields.tag);
  const defaultAidType = urlParams.get(apiFilterFields.defaultAidType);
  const defaultAidTypeVocabulary = urlParams.get(
    apiFilterFields.defaultAidTypeVocabulary
  );
  const currency = urlParams.get(apiFilterFields.currency);
  const defaultFinanceType = urlParams.get(apiFilterFields.defaultFinanceType);
  const defaultFlowType = urlParams.get(apiFilterFields.defaultFlowType);
  const defaultLanguage = urlParams.get(apiFilterFields.defaultLanguage);
  const defaultTiedStatus = urlParams.get(apiFilterFields.defaultTiedStatus);
  const transactionReceiverOrganisation = urlParams.get(
    apiFilterFields.transactionReceiverOrganisation
  );
  const reportingOrganisationType = urlParams.get(
    apiFilterFields.reportingOrganisationType
  );
  const collaborationType = urlParams.get(apiFilterFields.collaborationType);
  const secondaryReporter = urlParams.get(apiFilterFields.secondaryReporter);
  const sectorVocabulary = urlParams.get(apiFilterFields.sectorVocabulary);
  const policyMarker = urlParams.get(apiFilterFields.policyMarker);
  const transactionFlowType = urlParams.get(
    apiFilterFields.transactionFlowType
  );
  const transactionTiedStatus = urlParams.get(
    apiFilterFields.transactionTiedStatus
  );
  const transactionHumanitarian = urlParams.get(
    apiFilterFields.transactionHumanitarian
  );
  const transactionValueCurrency = urlParams.get(
    apiFilterFields.transactionValueCurrency
  );

  if (startDate) {
    updatedSelectedFilters.startDate = startDate;
  } else if (updatedSelectedFilters.startDate) {
    updatedSelectedFilters.startDate = "";
  }
  if (endDate) {
    updatedSelectedFilters.endDate = endDate;
  } else if (updatedSelectedFilters.endDate) {
    updatedSelectedFilters.endDate = "";
  }
  if (countries) {
    updatedSelectedFilters.countries = countries.split(",");
  } else if (updatedSelectedFilters.countries.length > 0) {
    updatedSelectedFilters.countries = [];
  }
  if (sectors) {
    updatedSelectedFilters.sectors = sectors.split(",");
  } else if (updatedSelectedFilters.sectors.length > 0) {
    updatedSelectedFilters.sectors = [];
  }
  if (publishers) {
    updatedSelectedFilters.publishers = publishers.split(",");
  } else if (updatedSelectedFilters.publishers.length > 0) {
    updatedSelectedFilters.publishers = [];
  }
  if (activityStatus) {
    updatedSelectedFilters.activityStatus = activityStatus.split(",");
  } else if (updatedSelectedFilters.activityStatus.length > 0) {
    updatedSelectedFilters.activityStatus = [];
  }
  if (amountMin) {
    updatedSelectedFilters.amountMin = parseInt(amountMin, 10);
  } else if (updatedSelectedFilters.amountMin) {
    updatedSelectedFilters.amountMin = 0;
  }
  if (amountMax) {
    updatedSelectedFilters.amountMax = parseInt(amountMax, 10);
  } else if (updatedSelectedFilters.amountMax) {
    updatedSelectedFilters.amountMax = 0;
  }
  if (transactionType) {
    const fTType = find(TRANSACTION_TYPES, {
      code: parseInt(transactionType, 10),
    });
    if (fTType) {
      updatedSelectedFilters.transactionType = fTType;
    } else {
      updatedSelectedFilters.transactionType = TRANSACTION_TYPES[0];
    }
  } else if (updatedSelectedFilters.transactionType) {
    updatedSelectedFilters.transactionType = TRANSACTION_TYPES[0];
  }
  if (search) {
    updatedSelectedFilters.search = search;
  } else if (updatedSelectedFilters.search) {
    updatedSelectedFilters.search = "";
  }
  if (hasSdg) {
    updatedSelectedFilters.hasSdg = hasSdg;
  } else if (updatedSelectedFilters.hasSdg) {
    updatedSelectedFilters.hasSdg = "";
  }
  if (sdgs) {
    updatedSelectedFilters.sdgs = sdgs.split(",");
  } else if (updatedSelectedFilters.sdgs.length > 0) {
    updatedSelectedFilters.sdgs = [];
  }
  if (activityScope) {
    updatedSelectedFilters.activityScope = activityScope.split(",");
  } else if (updatedSelectedFilters.activityScope.length > 0) {
    updatedSelectedFilters.activityScope = [];
  }
  if (documentLinkCategory) {
    updatedSelectedFilters.documentLinkCategory =
      documentLinkCategory.split(",");
  } else if (updatedSelectedFilters.documentLinkCategory.length > 0) {
    updatedSelectedFilters.documentLinkCategory = [];
  }
  if (hierarchy) {
    updatedSelectedFilters.hierarchy = hierarchy.split(",");
  } else if (updatedSelectedFilters.hierarchy.length > 0) {
    updatedSelectedFilters.hierarchy = [];
  }
  if (humanitarianScopeVocabulary) {
    updatedSelectedFilters.humanitarianScopeVocabulary =
      humanitarianScopeVocabulary.split(",");
  } else if (updatedSelectedFilters.humanitarianScopeVocabulary.length > 0) {
    updatedSelectedFilters.humanitarianScopeVocabulary = [];
  }
  if (humanitarianScopeType) {
    updatedSelectedFilters.humanitarianScopeType =
      humanitarianScopeType.split(",");
  } else if (updatedSelectedFilters.humanitarianScopeType.length > 0) {
    updatedSelectedFilters.humanitarianScopeType = [];
  }
  if (iatiVersion) {
    updatedSelectedFilters.iatiVersion = iatiVersion.split(",");
  } else if (updatedSelectedFilters.iatiVersion.length > 0) {
    updatedSelectedFilters.iatiVersion = [];
  }
  if (otherIdentifierType) {
    updatedSelectedFilters.otherIdentifierType = otherIdentifierType.split(",");
  } else if (updatedSelectedFilters.otherIdentifierType.length > 0) {
    updatedSelectedFilters.otherIdentifierType = [];
  }
  if (tag) {
    updatedSelectedFilters.tag = tag.split(",");
  } else if (updatedSelectedFilters.tag.length > 0) {
    updatedSelectedFilters.tag = [];
  }
  if (defaultAidType) {
    updatedSelectedFilters.defaultAidType = defaultAidType.split(",");
  } else if (updatedSelectedFilters.defaultAidType.length > 0) {
    updatedSelectedFilters.defaultAidType = [];
  }
  if (defaultAidTypeVocabulary) {
    updatedSelectedFilters.defaultAidTypeVocabulary =
      defaultAidTypeVocabulary.split(",");
  } else if (updatedSelectedFilters.defaultAidTypeVocabulary.length > 0) {
    updatedSelectedFilters.defaultAidTypeVocabulary = [];
  }
  if (currency) {
    updatedSelectedFilters.currency = currency.split(",");
  } else if (updatedSelectedFilters.currency.length > 0) {
    updatedSelectedFilters.currency = [];
  }
  if (defaultFinanceType) {
    updatedSelectedFilters.defaultFinanceType = defaultFinanceType.split(",");
  } else if (updatedSelectedFilters.defaultFinanceType.length > 0) {
    updatedSelectedFilters.defaultFinanceType = [];
  }
  if (defaultFlowType) {
    updatedSelectedFilters.defaultFlowType = defaultFlowType.split(",");
  } else if (updatedSelectedFilters.defaultFlowType.length > 0) {
    updatedSelectedFilters.defaultFlowType = [];
  }
  if (defaultLanguage) {
    updatedSelectedFilters.defaultLanguage = defaultLanguage.split(",");
  } else if (updatedSelectedFilters.defaultLanguage.length > 0) {
    updatedSelectedFilters.defaultLanguage = [];
  }
  if (defaultTiedStatus) {
    updatedSelectedFilters.defaultTiedStatus = defaultTiedStatus.split(",");
  } else if (updatedSelectedFilters.defaultTiedStatus.length > 0) {
    updatedSelectedFilters.defaultTiedStatus = [];
  }
  if (transactionReceiverOrganisation) {
    updatedSelectedFilters.transactionReceiverOrganisation =
      transactionReceiverOrganisation.split(",");
  } else if (
    updatedSelectedFilters.transactionReceiverOrganisation.length > 0
  ) {
    updatedSelectedFilters.transactionReceiverOrganisation = [];
  }
  if (reportingOrganisationType) {
    updatedSelectedFilters.reportingOrganisationType =
      reportingOrganisationType.split(",");
  } else if (updatedSelectedFilters.reportingOrganisationType.length > 0) {
    updatedSelectedFilters.reportingOrganisationType = [];
  }
  if (collaborationType) {
    updatedSelectedFilters.collaborationType = collaborationType.split(",");
  } else if (updatedSelectedFilters.collaborationType.length > 0) {
    updatedSelectedFilters.collaborationType = [];
  }
  if (secondaryReporter) {
    updatedSelectedFilters.secondaryReporter = secondaryReporter.split(",");
  } else if (updatedSelectedFilters.secondaryReporter.length > 0) {
    updatedSelectedFilters.secondaryReporter = [];
  }
  if (sectorVocabulary) {
    updatedSelectedFilters.sectorVocabulary = sectorVocabulary.split(",");
  } else if (updatedSelectedFilters.sectorVocabulary.length > 0) {
    updatedSelectedFilters.sectorVocabulary = [];
  }
  if (policyMarker) {
    updatedSelectedFilters.policyMarker = policyMarker.split(",");
  } else if (updatedSelectedFilters.policyMarker.length > 0) {
    updatedSelectedFilters.policyMarker = [];
  }
  if (transactionFlowType) {
    updatedSelectedFilters.transactionFlowType = transactionFlowType.split(",");
  } else if (updatedSelectedFilters.transactionFlowType.length > 0) {
    updatedSelectedFilters.transactionFlowType = [];
  }
  if (transactionTiedStatus) {
    updatedSelectedFilters.transactionTiedStatus =
      transactionTiedStatus.split(",");
  } else if (updatedSelectedFilters.transactionTiedStatus.length > 0) {
    updatedSelectedFilters.transactionTiedStatus = [];
  }
  if (transactionHumanitarian) {
    updatedSelectedFilters.transactionHumanitarian =
      transactionHumanitarian.split(",");
  } else if (updatedSelectedFilters.transactionHumanitarian.length > 0) {
    updatedSelectedFilters.transactionHumanitarian = [];
  }
  if (transactionValueCurrency) {
    updatedSelectedFilters.transactionValueCurrency =
      transactionValueCurrency.split(",");
  } else if (updatedSelectedFilters.transactionValueCurrency.length > 0) {
    updatedSelectedFilters.transactionValueCurrency = [];
  }
  return updatedSelectedFilters;
}

export const getUrlParamsFromSelectedFilters = (
  selectedFilters: AppliedFiltersStateModel,
  urlParams: URLSearchParams
) => {
  if (selectedFilters.startDate) {
    urlParams.set(apiFilterFields.startDate, selectedFilters.startDate);
  } else {
    urlParams.delete(apiFilterFields.startDate);
  }
  if (selectedFilters.endDate) {
    urlParams.set(apiFilterFields.endDate, selectedFilters.endDate);
  } else {
    urlParams.delete(apiFilterFields.endDate);
  }
  if (selectedFilters.countries.length > 0) {
    urlParams.set(apiFilterFields.country, selectedFilters.countries.join(","));
  } else {
    urlParams.delete(apiFilterFields.country);
  }
  if (selectedFilters.sectors.length > 0) {
    urlParams.set(apiFilterFields.sector, selectedFilters.sectors.join(","));
  } else {
    urlParams.delete(apiFilterFields.sector);
  }
  if (selectedFilters.publishers.length > 0) {
    urlParams.set(
      apiFilterFields.publisher,
      selectedFilters.publishers.join(",")
    );
  } else {
    urlParams.delete(apiFilterFields.publisher);
  }
  if (selectedFilters.activityStatus.length > 0) {
    urlParams.set(
      apiFilterFields.activityStatus,
      selectedFilters.activityStatus.join(",")
    );
  } else {
    urlParams.delete(apiFilterFields.activityStatus);
  }
  if (selectedFilters.amountMin > 0) {
    urlParams.set("amountMin", selectedFilters.amountMin.toString());
  } else {
    urlParams.delete("amountMin");
  }
  if (selectedFilters.amountMax > 0) {
    urlParams.set("amountMax", selectedFilters.amountMax.toString());
  } else {
    urlParams.delete("amountMax");
  }
  if (
    selectedFilters.transactionType &&
    selectedFilters.transactionType?.code !== TRANSACTION_TYPES[0].code
  ) {
    urlParams.set(
      apiFilterFields.transactionType,
      selectedFilters.transactionType.code.toString()
    );
  } else {
    urlParams.delete(apiFilterFields.transactionType);
  }
  if (selectedFilters.search) {
    urlParams.set("search", selectedFilters.search);
  } else {
    urlParams.delete("search");
  }
  if (selectedFilters.hasSdg) {
    urlParams.set(apiFilterFields.hasSdg, "1");
  } else {
    urlParams.delete(apiFilterFields.hasSdg);
  }
  if (selectedFilters.sdgs.length > 0) {
    urlParams.set(apiFilterFields.sdg, selectedFilters.sdgs.join(","));
  } else {
    urlParams.delete(apiFilterFields.sdg);
  }
  if (selectedFilters.activityScope.length > 0) {
    urlParams.set("activityScope", selectedFilters.activityScope.join(","));
  } else {
    urlParams.delete("activityScope");
  }
  if (selectedFilters.documentLinkCategory.length > 0) {
    urlParams.set(
      "documentLinkCategory",
      selectedFilters.documentLinkCategory.join(",")
    );
  } else {
    urlParams.delete("documentLinkCategory");
  }
  if (selectedFilters.hierarchy.length > 0) {
    urlParams.set("hierarchy", selectedFilters.hierarchy.join(","));
  } else {
    urlParams.delete("hierarchy");
  }
  if (selectedFilters.humanitarianScopeVocabulary.length > 0) {
    urlParams.set(
      "humanitarianScopeVocabulary",
      selectedFilters.humanitarianScopeVocabulary.join(",")
    );
  } else {
    urlParams.delete("humanitarianScopeVocabulary");
  }
  if (selectedFilters.humanitarianScopeType.length > 0) {
    urlParams.set(
      "humanitarianScopeType",
      selectedFilters.humanitarianScopeType.join(",")
    );
  } else {
    urlParams.delete("humanitarianScopeType");
  }
  if (selectedFilters.iatiVersion.length > 0) {
    urlParams.set("iatiVersion", selectedFilters.iatiVersion.join(","));
  } else {
    urlParams.delete("iatiVersion");
  }
  if (selectedFilters.otherIdentifierType.length > 0) {
    urlParams.set(
      "otherIdentifierType",
      selectedFilters.otherIdentifierType.join(",")
    );
  } else {
    urlParams.delete("otherIdentifierType");
  }
  if (selectedFilters.tag.length > 0) {
    urlParams.set("tag", selectedFilters.tag.join(","));
  } else {
    urlParams.delete("tag");
  }
  if (selectedFilters.defaultAidType.length > 0) {
    urlParams.set("defaultAidType", selectedFilters.defaultAidType.join(","));
  } else {
    urlParams.delete("defaultAidType");
  }
  if (selectedFilters.defaultAidTypeVocabulary.length > 0) {
    urlParams.set(
      "defaultAidTypeVocabulary",
      selectedFilters.defaultAidTypeVocabulary.join(",")
    );
  } else {
    urlParams.delete("defaultAidTypeVocabulary");
  }
  if (selectedFilters.currency.length > 0) {
    urlParams.set("currency", selectedFilters.currency.join(","));
  } else {
    urlParams.delete("currency");
  }
  if (selectedFilters.defaultFinanceType.length > 0) {
    urlParams.set(
      "defaultFinanceType",
      selectedFilters.defaultFinanceType.join(",")
    );
  } else {
    urlParams.delete("defaultFinanceType");
  }
  if (selectedFilters.defaultFlowType.length > 0) {
    urlParams.set("defaultFlowType", selectedFilters.defaultFlowType.join(","));
  } else {
    urlParams.delete("defaultFlowType");
  }
  if (selectedFilters.defaultLanguage.length > 0) {
    urlParams.set("defaultLanguage", selectedFilters.defaultLanguage.join(","));
  } else {
    urlParams.delete("defaultLanguage");
  }
  if (selectedFilters.defaultTiedStatus.length > 0) {
    urlParams.set(
      "defaultTiedStatus",
      selectedFilters.defaultTiedStatus.join(",")
    );
  } else {
    urlParams.delete("defaultTiedStatus");
  }
  if (selectedFilters.transactionReceiverOrganisation.length > 0) {
    urlParams.set(
      "transactionReceiverOrganisation",
      selectedFilters.transactionReceiverOrganisation.join(",")
    );
  } else {
    urlParams.delete("transactionReceiverOrganisation");
  }
  if (selectedFilters.reportingOrganisationType.length > 0) {
    urlParams.set(
      "reportingOrganisationType",
      selectedFilters.reportingOrganisationType.join(",")
    );
  } else {
    urlParams.delete("reportingOrganisationType");
  }
  if (selectedFilters.collaborationType.length > 0) {
    urlParams.set(
      "collaborationType",
      selectedFilters.collaborationType.join(",")
    );
  } else {
    urlParams.delete("collaborationType");
  }
  if (selectedFilters.secondaryReporter.length > 0) {
    urlParams.set(
      "secondaryReporter",
      selectedFilters.secondaryReporter.join(",")
    );
  } else {
    urlParams.delete("secondaryReporter");
  }
  if (selectedFilters.sectorVocabulary.length > 0) {
    urlParams.set(
      "sectorVocabulary",
      selectedFilters.sectorVocabulary.join(",")
    );
  } else {
    urlParams.delete("sectorVocabulary");
  }
  if (selectedFilters.policyMarker.length > 0) {
    urlParams.set("policyMarker", selectedFilters.policyMarker.join(","));
  } else {
    urlParams.delete("policyMarker");
  }
  if (selectedFilters.transactionFlowType.length > 0) {
    urlParams.set(
      "transactionFlowType",
      selectedFilters.transactionFlowType.join(",")
    );
  } else {
    urlParams.delete("transactionFlowType");
  }
  if (selectedFilters.transactionTiedStatus.length > 0) {
    urlParams.set(
      "transactionTiedStatus",
      selectedFilters.transactionTiedStatus.join(",")
    );
  } else {
    urlParams.delete("transactionTiedStatus");
  }
  if (selectedFilters.transactionHumanitarian.length > 0) {
    urlParams.set(
      "transactionHumanitarian",
      selectedFilters.transactionHumanitarian.join(",")
    );
  } else {
    urlParams.delete("transactionHumanitarian");
  }
  if (selectedFilters.transactionValueCurrency.length > 0) {
    urlParams.set(
      "transactionValueCurrency",
      selectedFilters.transactionValueCurrency.join(",")
    );
  } else {
    urlParams.delete("transactionValueCurrency");
  }

  return urlParams;
};

export function useUrlFilters() {
  const location = useLocation();
  const [urlParams, setUrlParams] = useSearchParams();

  const [latestNavQS, setLatestNavQS] = React.useState<string>("");
  const lastChangeSource = React.useRef<"store" | "url" | null>(null);

  const selectedFilters = useStoreState((state) => state.appliedFilters);
  const setSelectedFilters = useStoreActions(
    (actions) => actions.appliedFilters.setAllFilters
  );

  const inFilterPages = React.useMemo(
    () =>
      [
        "/explorer",
        "/explorer/locations",
        "/explorer/organisations",
        "/explorer/sectors",
        "/explorer/activities",
      ].indexOf(location.pathname) > -1 ||
      location.pathname.indexOf("/explorer/locations/") > -1 ||
      location.pathname.indexOf("/explorer/organisations/") > -1 ||
      location.pathname.indexOf("/explorer/sectors/") > -1 ||
      location.pathname.indexOf("/explorer/activities/") > -1,
    [location.pathname]
  );

  const inActivityPage = React.useMemo(
    () => location.pathname.indexOf("/explorer/activities/") > -1,
    [location.pathname]
  );

  const locationSearchWithoutExpandedBlock = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("e");
    return searchParams.toString();
  }, [location.search]);

  const [latestSearch, setLatestSearch] = React.useState<string>(
    locationSearchWithoutExpandedBlock
  );

  // On mount: sync store from URL
  useComponentWillMount({
    action: () => {
      lastChangeSource.current = "url";
      const updatedSelectedFilters = { ...selectedFilters };
      const urlParams = new URLSearchParams(location.search);
      // ...existing code...
      const startDate = urlParams.get(apiFilterFields.startDate);
      const endDate = urlParams.get(apiFilterFields.endDate);
      const countries = urlParams.get(apiFilterFields.country);
      const sectors = urlParams.get(apiFilterFields.sector);
      const publishers = urlParams.get(apiFilterFields.publisher);
      const activityStatus = urlParams.get(apiFilterFields.activityStatus);
      const amountMin = urlParams.get(apiFilterFields.amountMin);
      const amountMax = urlParams.get(apiFilterFields.amountMax);
      const transactionType = urlParams.get(apiFilterFields.transactionType);
      const search = urlParams.get(apiFilterFields.search);
      const hasSdg = urlParams.get(apiFilterFields.hasSdg);
      const sdgs = urlParams.get(apiFilterFields.sdg);
      const activityScope = urlParams.get(apiFilterFields.activityScope);
      const documentLinkCategory = urlParams.get(
        apiFilterFields.documentLinkCategory
      );
      const hierarchy = urlParams.get(apiFilterFields.hierarchy);
      const humanitarianScopeVocabulary = urlParams.get(
        apiFilterFields.humanitarianScopeVocabulary
      );
      const humanitarianScopeType = urlParams.get(
        apiFilterFields.humanitarianScopeType
      );
      const iatiVersion = urlParams.get(apiFilterFields.iatiVersion);
      const otherIdentifierType = urlParams.get(
        apiFilterFields.otherIdentifierType
      );
      const tag = urlParams.get(apiFilterFields.tag);
      const defaultAidType = urlParams.get(apiFilterFields.defaultAidType);
      const defaultAidTypeVocabulary = urlParams.get(
        apiFilterFields.defaultAidTypeVocabulary
      );
      const currency = urlParams.get(apiFilterFields.currency);
      const defaultFinanceType = urlParams.get(
        apiFilterFields.defaultFinanceType
      );
      const defaultFlowType = urlParams.get(apiFilterFields.defaultFlowType);
      const defaultLanguage = urlParams.get(apiFilterFields.defaultLanguage);
      const defaultTiedStatus = urlParams.get(
        apiFilterFields.defaultTiedStatus
      );
      const transactionReceiverOrganisation = urlParams.get(
        apiFilterFields.transactionReceiverOrganisation
      );
      const reportingOrganisationType = urlParams.get(
        apiFilterFields.reportingOrganisationType
      );
      const collaborationType = urlParams.get(
        apiFilterFields.collaborationType
      );
      const secondaryReporter = urlParams.get(
        apiFilterFields.secondaryReporter
      );
      const sectorVocabulary = urlParams.get(apiFilterFields.sectorVocabulary);
      const policyMarker = urlParams.get(apiFilterFields.policyMarker);
      const transactionFlowType = urlParams.get(
        apiFilterFields.transactionFlowType
      );
      const transactionTiedStatus = urlParams.get(
        apiFilterFields.transactionTiedStatus
      );
      const transactionHumanitarian = urlParams.get(
        apiFilterFields.transactionHumanitarian
      );
      const transactionValueCurrency = urlParams.get(
        apiFilterFields.transactionValueCurrency
      );

      if (startDate) {
        updatedSelectedFilters.startDate = startDate;
      }
      if (endDate) {
        updatedSelectedFilters.endDate = endDate;
      }
      if (countries) {
        updatedSelectedFilters.countries = countries.split(",");
      }
      if (sectors) {
        updatedSelectedFilters.sectors = sectors.split(",");
      }
      if (publishers) {
        updatedSelectedFilters.publishers = publishers.split(",");
      }
      if (activityStatus) {
        updatedSelectedFilters.activityStatus = activityStatus.split(",");
      }
      if (amountMin) {
        updatedSelectedFilters.amountMin = parseInt(amountMin, 10);
      }
      if (amountMax) {
        updatedSelectedFilters.amountMax = parseInt(amountMax, 10);
      }
      if (transactionType) {
        const fTType = find(TRANSACTION_TYPES, {
          code: parseInt(transactionType, 10),
        });
        if (fTType) {
          updatedSelectedFilters.transactionType = fTType;
        }
      }
      if (search) {
        updatedSelectedFilters.search = search;
      }
      if (hasSdg) {
        updatedSelectedFilters.hasSdg = hasSdg;
      }
      if (sdgs) {
        updatedSelectedFilters.sdgs = sdgs.split(",");
      }
      if (activityScope) {
        updatedSelectedFilters.activityScope = activityScope.split(",");
      }
      if (documentLinkCategory) {
        updatedSelectedFilters.documentLinkCategory =
          documentLinkCategory.split(",");
      }
      if (hierarchy) {
        updatedSelectedFilters.hierarchy = hierarchy.split(",");
      }
      if (humanitarianScopeVocabulary) {
        updatedSelectedFilters.humanitarianScopeVocabulary =
          humanitarianScopeVocabulary.split(",");
      }
      if (humanitarianScopeType) {
        updatedSelectedFilters.humanitarianScopeType =
          humanitarianScopeType.split(",");
      }
      if (iatiVersion) {
        updatedSelectedFilters.iatiVersion = iatiVersion.split(",");
      }
      if (otherIdentifierType) {
        updatedSelectedFilters.otherIdentifierType =
          otherIdentifierType.split(",");
      }
      if (tag) {
        updatedSelectedFilters.tag = tag.split(",");
      }
      if (defaultAidType) {
        updatedSelectedFilters.defaultAidType = defaultAidType.split(",");
      }
      if (defaultAidTypeVocabulary) {
        updatedSelectedFilters.defaultAidTypeVocabulary =
          defaultAidTypeVocabulary.split(",");
      }
      if (currency) {
        updatedSelectedFilters.currency = currency.split(",");
      }
      if (defaultFinanceType) {
        updatedSelectedFilters.defaultFinanceType =
          defaultFinanceType.split(",");
      }
      if (defaultFlowType) {
        updatedSelectedFilters.defaultFlowType = defaultFlowType.split(",");
      }
      if (defaultLanguage) {
        updatedSelectedFilters.defaultLanguage = defaultLanguage.split(",");
      }
      if (defaultTiedStatus) {
        updatedSelectedFilters.defaultTiedStatus = defaultTiedStatus.split(",");
      }
      if (transactionReceiverOrganisation) {
        updatedSelectedFilters.transactionReceiverOrganisation =
          transactionReceiverOrganisation.split(",");
      }
      if (reportingOrganisationType) {
        updatedSelectedFilters.reportingOrganisationType =
          reportingOrganisationType.split(",");
      }
      if (collaborationType) {
        updatedSelectedFilters.collaborationType = collaborationType.split(",");
      }
      if (secondaryReporter) {
        updatedSelectedFilters.secondaryReporter = secondaryReporter.split(",");
      }
      if (sectorVocabulary) {
        updatedSelectedFilters.sectorVocabulary = sectorVocabulary.split(",");
      }
      if (policyMarker) {
        updatedSelectedFilters.policyMarker = policyMarker.split(",");
      }
      if (transactionFlowType) {
        updatedSelectedFilters.transactionFlowType =
          transactionFlowType.split(",");
      }
      if (transactionTiedStatus) {
        updatedSelectedFilters.transactionTiedStatus =
          transactionTiedStatus.split(",");
      }
      if (transactionHumanitarian) {
        updatedSelectedFilters.transactionHumanitarian =
          transactionHumanitarian.split(",");
      }
      if (transactionValueCurrency) {
        updatedSelectedFilters.transactionValueCurrency =
          transactionValueCurrency.split(",");
      }

      setSelectedFilters(updatedSelectedFilters);
    },
  });

  useUnmount(() => setSelectedFilters(defaultAppliedFilters));

  // Store -> URL sync (always check and update if not in sync)
  useUpdateEffect(() => {
    if (!inFilterPages) return;
    if (inActivityPage) {
      setUrlParams("");
      setLatestNavQS("");
      return;
    }

    const newUrlParams = getUrlParamsFromSelectedFilters(
      selectedFilters,
      new URLSearchParams(urlParams.toString())
    );
    const queryString = decodeURIComponent(newUrlParams.toString());
    // Only update if not in sync
    if (
      latestNavQS !== queryString ||
      urlParams.toString() !== newUrlParams.toString()
    ) {
      lastChangeSource.current = "store";
      setLatestNavQS(queryString);
      setUrlParams(newUrlParams);
    }
  }, [selectedFilters, location.pathname]);

  // URL -> Store sync (always check and update if not in sync)
  useUpdateEffect(() => {
    if (!inFilterPages) return;
    const urlQS = decodeURIComponent(urlParams.toString());
    // Only update if not in sync
    const normalized = getNormalizedFiltersFromUrlParams(
      urlParams,
      selectedFilters
    );
    if (latestNavQS !== urlQS || !isEqual(selectedFilters, normalized)) {
      lastChangeSource.current = "url";
      onLocationSearchChange(urlParams, selectedFilters, setSelectedFilters);
      setLatestNavQS(urlQS);
    }
  }, [urlParams, location.pathname]);

  // Activity page special handling
  useUpdateEffect(() => {
    if (!inActivityPage) return;
    if (locationSearchWithoutExpandedBlock !== latestSearch) {
      onLocationSearchChange(urlParams, selectedFilters, setSelectedFilters);
    }
    setLatestSearch(locationSearchWithoutExpandedBlock);
  }, [locationSearchWithoutExpandedBlock]);

  useUpdateEffect(() => {
    setLatestNavQS(urlParams.toString());
  }, [location.pathname]);

  return null;
}

export function onLocationSearchChange(
  urlParams: URLSearchParams,
  selectedFilters: AppliedFiltersStateModel,
  setSelectedFilters: ActionCreator<AppliedFiltersStateModel>
) {
  const updatedSelectedFilters = { ...selectedFilters };
  const startDate = urlParams.get(apiFilterFields.startDate);
  const endDate = urlParams.get(apiFilterFields.endDate);
  const countries = urlParams.get(apiFilterFields.country);
  const sectors = urlParams.get(apiFilterFields.sector);
  const publishers = urlParams.get(apiFilterFields.publisher);
  const activityStatus = urlParams.get(apiFilterFields.activityStatus);
  const amountMin = urlParams.get(apiFilterFields.amountMin);
  const amountMax = urlParams.get(apiFilterFields.amountMax);
  const transactionType = urlParams.get(apiFilterFields.transactionType);
  const search = urlParams.get(apiFilterFields.search);
  const hasSdg = urlParams.get(apiFilterFields.hasSdg);
  const sdgs = urlParams.get(apiFilterFields.sdg);
  const activityScope = urlParams.get(apiFilterFields.activityScope);
  const documentLinkCategory = urlParams.get(
    apiFilterFields.documentLinkCategory
  );
  const hierarchy = urlParams.get(apiFilterFields.hierarchy);
  const humanitarianScopeVocabulary = urlParams.get(
    apiFilterFields.humanitarianScopeVocabulary
  );
  const humanitarianScopeType = urlParams.get(
    apiFilterFields.humanitarianScopeType
  );
  const iatiVersion = urlParams.get(apiFilterFields.iatiVersion);
  const otherIdentifierType = urlParams.get(
    apiFilterFields.otherIdentifierType
  );
  const tag = urlParams.get(apiFilterFields.tag);
  const defaultAidType = urlParams.get(apiFilterFields.defaultAidType);
  const defaultAidTypeVocabulary = urlParams.get(
    apiFilterFields.defaultAidTypeVocabulary
  );
  const currency = urlParams.get(apiFilterFields.currency);
  const defaultFinanceType = urlParams.get(apiFilterFields.defaultFinanceType);
  const defaultFlowType = urlParams.get(apiFilterFields.defaultFlowType);
  const defaultLanguage = urlParams.get(apiFilterFields.defaultLanguage);
  const defaultTiedStatus = urlParams.get(apiFilterFields.defaultTiedStatus);
  const transactionReceiverOrganisation = urlParams.get(
    apiFilterFields.transactionReceiverOrganisation
  );
  const reportingOrganisationType = urlParams.get(
    apiFilterFields.reportingOrganisationType
  );
  const collaborationType = urlParams.get(apiFilterFields.collaborationType);
  const secondaryReporter = urlParams.get(apiFilterFields.secondaryReporter);
  const sectorVocabulary = urlParams.get(apiFilterFields.sectorVocabulary);
  const policyMarker = urlParams.get(apiFilterFields.policyMarker);
  const transactionFlowType = urlParams.get(
    apiFilterFields.transactionFlowType
  );
  const transactionTiedStatus = urlParams.get(
    apiFilterFields.transactionTiedStatus
  );
  const transactionHumanitarian = urlParams.get(
    apiFilterFields.transactionHumanitarian
  );
  const transactionValueCurrency = urlParams.get(
    apiFilterFields.transactionValueCurrency
  );

  if (startDate) {
    updatedSelectedFilters.startDate = startDate;
  } else if (updatedSelectedFilters.startDate) {
    updatedSelectedFilters.startDate = "";
  }
  if (endDate) {
    updatedSelectedFilters.endDate = endDate;
  } else if (updatedSelectedFilters.endDate) {
    updatedSelectedFilters.endDate = "";
  }
  if (countries) {
    updatedSelectedFilters.countries = countries.split(",");
  } else if (updatedSelectedFilters.countries.length > 0) {
    updatedSelectedFilters.countries = [];
  }
  if (sectors) {
    updatedSelectedFilters.sectors = sectors.split(",");
  } else if (updatedSelectedFilters.sectors.length > 0) {
    updatedSelectedFilters.sectors = [];
  }
  if (publishers) {
    updatedSelectedFilters.publishers = publishers.split(",");
  } else if (updatedSelectedFilters.publishers.length > 0) {
    updatedSelectedFilters.publishers = [];
  }
  if (activityStatus) {
    updatedSelectedFilters.activityStatus = activityStatus.split(",");
  } else if (updatedSelectedFilters.activityStatus.length > 0) {
    updatedSelectedFilters.activityStatus = [];
  }
  if (amountMin) {
    updatedSelectedFilters.amountMin = parseInt(amountMin, 10);
  } else if (updatedSelectedFilters.amountMin) {
    updatedSelectedFilters.amountMin = 0;
  }
  if (amountMax) {
    updatedSelectedFilters.amountMax = parseInt(amountMax, 10);
  } else if (updatedSelectedFilters.amountMax) {
    updatedSelectedFilters.amountMax = 0;
  }
  if (transactionType) {
    const fTType = find(TRANSACTION_TYPES, {
      code: parseInt(transactionType, 10),
    });
    if (fTType) {
      updatedSelectedFilters.transactionType = fTType;
    } else {
      updatedSelectedFilters.transactionType = TRANSACTION_TYPES[0];
    }
  } else if (updatedSelectedFilters.transactionType) {
    updatedSelectedFilters.transactionType = TRANSACTION_TYPES[0];
  }
  if (search) {
    updatedSelectedFilters.search = search;
  } else if (updatedSelectedFilters.search) {
    updatedSelectedFilters.search = "";
  }
  if (hasSdg) {
    updatedSelectedFilters.hasSdg = hasSdg;
  } else if (updatedSelectedFilters.hasSdg) {
    updatedSelectedFilters.hasSdg = "";
  }
  if (sdgs) {
    updatedSelectedFilters.sdgs = sdgs.split(",");
  } else if (updatedSelectedFilters.sdgs.length > 0) {
    updatedSelectedFilters.sdgs = [];
  }
  if (activityScope) {
    updatedSelectedFilters.activityScope = activityScope.split(",");
  } else if (updatedSelectedFilters.activityScope.length > 0) {
    updatedSelectedFilters.activityScope = [];
  }
  if (documentLinkCategory) {
    updatedSelectedFilters.documentLinkCategory =
      documentLinkCategory.split(",");
  } else if (updatedSelectedFilters.documentLinkCategory.length > 0) {
    updatedSelectedFilters.documentLinkCategory = [];
  }
  if (hierarchy) {
    updatedSelectedFilters.hierarchy = hierarchy.split(",");
  } else if (updatedSelectedFilters.hierarchy.length > 0) {
    updatedSelectedFilters.hierarchy = [];
  }
  if (humanitarianScopeVocabulary) {
    updatedSelectedFilters.humanitarianScopeVocabulary =
      humanitarianScopeVocabulary.split(",");
  } else if (updatedSelectedFilters.humanitarianScopeVocabulary.length > 0) {
    updatedSelectedFilters.humanitarianScopeVocabulary = [];
  }
  if (humanitarianScopeType) {
    updatedSelectedFilters.humanitarianScopeType =
      humanitarianScopeType.split(",");
  } else if (updatedSelectedFilters.humanitarianScopeType.length > 0) {
    updatedSelectedFilters.humanitarianScopeType = [];
  }
  if (iatiVersion) {
    updatedSelectedFilters.iatiVersion = iatiVersion.split(",");
  } else if (updatedSelectedFilters.iatiVersion.length > 0) {
    updatedSelectedFilters.iatiVersion = [];
  }
  if (otherIdentifierType) {
    updatedSelectedFilters.otherIdentifierType = otherIdentifierType.split(",");
  } else if (updatedSelectedFilters.otherIdentifierType.length > 0) {
    updatedSelectedFilters.otherIdentifierType = [];
  }
  if (tag) {
    updatedSelectedFilters.tag = tag.split(",");
  } else if (updatedSelectedFilters.tag.length > 0) {
    updatedSelectedFilters.tag = [];
  }
  if (defaultAidType) {
    updatedSelectedFilters.defaultAidType = defaultAidType.split(",");
  } else if (updatedSelectedFilters.defaultAidType.length > 0) {
    updatedSelectedFilters.defaultAidType = [];
  }
  if (defaultAidTypeVocabulary) {
    updatedSelectedFilters.defaultAidTypeVocabulary =
      defaultAidTypeVocabulary.split(",");
  } else if (updatedSelectedFilters.defaultAidTypeVocabulary.length > 0) {
    updatedSelectedFilters.defaultAidTypeVocabulary = [];
  }
  if (currency) {
    updatedSelectedFilters.currency = currency.split(",");
  } else if (updatedSelectedFilters.currency.length > 0) {
    updatedSelectedFilters.currency = [];
  }
  if (defaultFinanceType) {
    updatedSelectedFilters.defaultFinanceType = defaultFinanceType.split(",");
  } else if (updatedSelectedFilters.defaultFinanceType.length > 0) {
    updatedSelectedFilters.defaultFinanceType = [];
  }
  if (defaultFlowType) {
    updatedSelectedFilters.defaultFlowType = defaultFlowType.split(",");
  } else if (updatedSelectedFilters.defaultFlowType.length > 0) {
    updatedSelectedFilters.defaultFlowType = [];
  }
  if (defaultLanguage) {
    updatedSelectedFilters.defaultLanguage = defaultLanguage.split(",");
  } else if (updatedSelectedFilters.defaultLanguage.length > 0) {
    updatedSelectedFilters.defaultLanguage = [];
  }
  if (defaultTiedStatus) {
    updatedSelectedFilters.defaultTiedStatus = defaultTiedStatus.split(",");
  } else if (updatedSelectedFilters.defaultTiedStatus.length > 0) {
    updatedSelectedFilters.defaultTiedStatus = [];
  }
  if (transactionReceiverOrganisation) {
    updatedSelectedFilters.transactionReceiverOrganisation =
      transactionReceiverOrganisation.split(",");
  } else if (
    updatedSelectedFilters.transactionReceiverOrganisation.length > 0
  ) {
    updatedSelectedFilters.transactionReceiverOrganisation = [];
  }
  if (reportingOrganisationType) {
    updatedSelectedFilters.reportingOrganisationType =
      reportingOrganisationType.split(",");
  } else if (updatedSelectedFilters.reportingOrganisationType.length > 0) {
    updatedSelectedFilters.reportingOrganisationType = [];
  }
  if (collaborationType) {
    updatedSelectedFilters.collaborationType = collaborationType.split(",");
  } else if (updatedSelectedFilters.collaborationType.length > 0) {
    updatedSelectedFilters.collaborationType = [];
  }
  if (secondaryReporter) {
    updatedSelectedFilters.secondaryReporter = secondaryReporter.split(",");
  } else if (updatedSelectedFilters.secondaryReporter.length > 0) {
    updatedSelectedFilters.secondaryReporter = [];
  }
  if (sectorVocabulary) {
    updatedSelectedFilters.sectorVocabulary = sectorVocabulary.split(",");
  } else if (updatedSelectedFilters.sectorVocabulary.length > 0) {
    updatedSelectedFilters.sectorVocabulary = [];
  }
  if (policyMarker) {
    updatedSelectedFilters.policyMarker = policyMarker.split(",");
  } else if (updatedSelectedFilters.policyMarker.length > 0) {
    updatedSelectedFilters.policyMarker = [];
  }
  if (transactionFlowType) {
    updatedSelectedFilters.transactionFlowType = transactionFlowType.split(",");
  } else if (updatedSelectedFilters.transactionFlowType.length > 0) {
    updatedSelectedFilters.transactionFlowType = [];
  }
  if (transactionTiedStatus) {
    updatedSelectedFilters.transactionTiedStatus =
      transactionTiedStatus.split(",");
  } else if (updatedSelectedFilters.transactionTiedStatus.length > 0) {
    updatedSelectedFilters.transactionTiedStatus = [];
  }
  if (transactionHumanitarian) {
    updatedSelectedFilters.transactionHumanitarian =
      transactionHumanitarian.split(",");
  } else if (updatedSelectedFilters.transactionHumanitarian.length > 0) {
    updatedSelectedFilters.transactionHumanitarian = [];
  }
  if (transactionValueCurrency) {
    updatedSelectedFilters.transactionValueCurrency =
      transactionValueCurrency.split(",");
  } else if (updatedSelectedFilters.transactionValueCurrency.length > 0) {
    updatedSelectedFilters.transactionValueCurrency = [];
  }

  if (!isEqual(selectedFilters, updatedSelectedFilters)) {
    setSelectedFilters(updatedSelectedFilters);
  }
}
